/**
 * FIManagerList Component
 *
 * Sortable list of F&I managers with expandable rows.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { FiManager, SortConfig, SortField } from './types';
import { FIManagerRow } from './FIManagerRow';

interface FIManagerListProps {
  fiManagers: FiManager[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

const SORT_COLUMNS: Array<{
  field: SortField;
  shortLabel: string;
}> = [
  { field: 'name', shortLabel: 'Name' },
  { field: 'backEndGross', shortLabel: 'Gross' },
  { field: 'totalUnits', shortLabel: 'Units' },
  { field: 'pvr', shortLabel: 'PVR' },
];

export const FIManagerList: React.FC<FIManagerListProps> = ({
  fiManagers,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-blue-600" />
    );
  };

  if (fiManagers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No F&amp;I manager data available for this period.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {fiManagers.length} F&amp;I manager{fiManagers.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="bg-white rounded-b-lg">
        {fiManagers.map((mgr, index) => (
          <FIManagerRow
            key={mgr.fiManagerId}
            fiManager={mgr}
            isExpanded={expandedIds.has(mgr.fiManagerId)}
            onToggle={() => toggleExpanded(mgr.fiManagerId)}
            rank={index + 1}
          />
        ))}
      </div>
    </div>
  );
};
